// src/Teacher/ParentShowcase.js
/* "Show parents" — end of the Demo Sessions tab.
   During a free trial the teacher shares the screen and shows the parents why RoboMind,
   the courses and the packages, without leaving the dashboard.
   Courses and Packages reuse the home page sections, so prices stay the same everywhere. */
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress, faHeart, faBookOpen, faTags } from '@fortawesome/free-solid-svg-icons';
import ClassesSection from '../Home/Classessection';
import PricingArea from '../Home/PricingArea';
import { COURSES } from '../data/courses';
import './ParentShowcase.css';

const VIEWS = [
  { key: 'why', label: 'Why RoboMind', icon: faHeart },
  { key: 'courses', label: 'Courses', icon: faBookOpen },
  { key: 'packages', label: 'Packages', icon: faTags },
];

const PAYMENTS = ['Whish Money', 'OMT', 'BOB Finance', 'Western Union', 'Payment link'];

const BENEFITS = [
  { emoji: '👩‍🏫', title: 'Live 1-to-1 classes', text: 'Only your child and the teacher, live on Zoom. About 50 minutes per session.' },
  { emoji: '🗓️', title: 'Flexible schedule', text: 'You choose the days and times that fit around school. Most students take 2 sessions a week.' },
  { emoji: '🎓', title: 'Certificates', text: 'A certificate at the end of every course. Our program is accredited by STEM.org.', stem: true },
  { emoji: '💳', title: 'Easy payment', text: 'Pay from Lebanon or abroad. We send the payment details on WhatsApp.', pay: true },
  { emoji: '🧩', title: 'A real project every session', text: 'Every class ends with something your child built and can show you.' },
  { emoji: '📈', title: 'Progress reports', text: 'You get progress reports, and WhatsApp reminders before each class.' },
  { emoji: '🏆', title: 'Challenges & badges', text: 'Coding challenges, badges and a Wall of fame keep students motivated.' },
  { emoji: '🔄', title: 'Change teacher anytime', text: 'If another teacher is a better fit, you can ask for a change at any time.' },
  { emoji: '🤖', title: 'Robotics at home', text: 'For robotics courses, the kit is delivered to your home.' },
  { emoji: '📚', title: 'Materials forever', text: 'Lifetime access to the course materials and projects.' },
];

const fsElement = () => document.fullscreenElement || document.webkitFullscreenElement;

const ParentShowcase = () => {
  const [view, setView] = useState('why');
  const [full, setFull] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onChange = () => setFull(fsElement() === boxRef.current);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const toggleFull = () => {
    const el = boxRef.current;
    if (!el) return;
    try {
      if (fsElement()) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      else (el.requestFullscreen || el.webkitRequestFullscreen).call(el);
    } catch (e) {
      /* full screen not supported: the section still works normally */
    }
  };

  return (
    <section className={`rmtps ${full ? 'is-full' : ''}`} ref={boxRef} aria-labelledby="rmtps-title">
      <header className="rmtps-head">
        <div>
          <span className="rmtps-eyebrow">Show parents</span>
          <h2 id="rmtps-title" className="rmtps-title">
            At the end of the trial, <span>share your screen</span>
          </h2>
          <p className="rmtps-sub">Why RoboMind, our courses and the packages, all in one place.</p>
        </div>
        <button type="button" className="rmtps-full" onClick={toggleFull}>
          <FontAwesomeIcon icon={full ? faCompress : faExpand} /> {full ? 'Exit full screen' : 'Full screen'}
        </button>
      </header>

      <div className="rmtps-tabs" role="tablist" aria-label="What to show">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            className={`rmtps-tab ${view === v.key ? 'is-on' : ''}`}
            onClick={() => setView(v.key)}
          >
            <FontAwesomeIcon icon={v.icon} /> {v.label}
          </button>
        ))}
      </div>

      <div className="rmtps-panel" role="tabpanel">
        {view === 'why' && (
          <div className="rmtps-why">
            <ul className="rmtps-facts" aria-label="RoboMind in short">
              <li>
                <strong>{COURSES.length}</strong> courses
              </li>
              <li>
                <strong>6–17</strong> years old
              </li>
              <li>
                <strong>1-to-1</strong> live online
              </li>
              <li>
                <strong>STEM.org</strong> accredited
              </li>
            </ul>

            <ul className="rmtps-grid">
              {BENEFITS.map((b) => (
                <li key={b.title} className={`rmtps-card ${b.pay || b.stem ? 'is-wide' : ''}`}>
                  <span className="rmtps-emoji" aria-hidden="true">
                    {b.emoji}
                  </span>
                  <div className="rmtps-card-body">
                    <h3>{b.title}</h3>
                    <p>{b.text}</p>
                    {b.pay && (
                      <ul className="rmtps-chips" aria-label="Payment methods">
                        {PAYMENTS.map((p) => (
                          <li key={p}>{p}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {b.stem && (
                    <>
                      <img className="rmtps-stem rmtps-stem--dark" src="/assets/img/hero/stem-accredited.png" alt="STEM.org Accredited" loading="lazy" />
                      <img className="rmtps-stem rmtps-stem--light" src="/assets/img/hero/stem-accredited-lightmode.png" alt="STEM.org Accredited" loading="lazy" />
                    </>
                  )}
                </li>
              ))}
            </ul>

            <p className="rmtps-next">
              Next step: message us on WhatsApp, and we’ll help you choose the right course and package 💬
            </p>
          </div>
        )}
        {view === 'courses' && <ClassesSection />}
        {view === 'packages' && <PricingArea />}
      </div>
    </section>
  );
};

export default ParentShowcase;
